import axios from 'axios'
import api from '../axios'

/**
 * @description 通用模块 API 集合
 */
export const baseApi = {
  /**
   * @description 更新内容统计数据
   * @param {Object} params - 统计参数
   * @param {number|string} params.contentId - 内容 ID
   * @param {number} params.statType - 统计类型
   * @reason 内容统计属于非阻断型埋点，失败时不应向用户展示全局网络错误提示
   * @returns {Promise<Object>} 统计结果
   */
  updateContentStat: (params) =>
    api.post('/p/store/content/updateStat', params, {
      showError: false,
    }),

  /**
   * @description 获取购物车详情
   * @returns {Promise<Object>} 购物车信息
   */
  getCart: () => api.get('/store/cart'),

  /**
   * @description 搜索产品列表
   * @param {Object} params - 请求参数
   * @param {number} params.current - 当前页码
   * @param {number} params.size - 每页大小
   * @param {number} params.sort - 排序方式（排序 1 按拖拽排序 2 按传入商品id排序 3 按置顶销量上架时间排序 4 价格 5 排序参数）
   * @param {number} [params.sortType] - 排序方向：1 升序 2 降序
   * @param {number} [params.labelPick] - 标签：1 甄选好物 2 新品  3 热销
   * @param {Array<number>} params.prodIds - 产品ID数组（可选）
   * @param {number} params.primaryCategoryId - 主分类ID
   * @returns {Promise<Object>} 搜索结果
   */
  searchProducts: (params) =>
    api.post('/search/page/v1', params, {
      params: {
        current: params.current || 1,
        size: params.size || 10,
      },
    }),

  /**
   * @description 全局搜索（仅商品 · 仅已上架且未退市 · 品类/型号/名称/简介/描述/参数 模糊）
   * @param {Object} params - 请求参数
   * @param {string} params.keyword - 搜索关键词
   * @param {number} [params.current=1] - 页码
   * @param {number} [params.size=20] - 每页商品数，最大 20
   * @returns {Promise<Array>} 形如 [{ serviceType: 1, total, current, size, globalSearchList }]
   */
  globalSearch: (params) => api.post('/p/store/search/global', params),

  /**
   * @description 获取热搜列表（页面加载，失败静默）
   * @param {number} type - 搜索类型 1:商品 2:店铺
   * @returns {Promise<Array>} 热搜列表
   */
  getHotSearch: (type = 1) =>
    api.get('/store/search/hot-search', { params: { type }, showError: false }),

  /**
   * @description 获取当前用户搜索历史（后端持久化，页面加载失败静默）
   * @returns {Promise<Array>} 搜索历史列表，元素形如 { id, keyword }
   */
  getSearchKeywords: () => api.get('/p/store/search/history', { showError: false }),

  /**
   * @description 记录一条搜索关键词到后端历史（同词去重，失败静默由前端本地兜底）
   * @param {string} keyword - 搜索关键词
   * @returns {Promise<Object>} 记录后的最新历史列表
   */
  recordSearchKeyword: (keyword) =>
    api.post('/p/store/search/history', { keyword }, { showError: false }),

  /**
   * @description 清空当前用户全部搜索历史（后端逻辑删）
   * @returns {Promise<Object>}
   */
  deleteSearchKeywords: () => api.delete('/p/store/search/history'),

  /**
   * @description 获取商城分类信息（页面加载，失败静默）
   * @deprecated 请优先使用 getCategoryProdTree，本接口仅保留兼容
   * @returns {Promise<Object>} 分类信息
   */
  getCategoryInfo: () => api.get('/category/categoryInfo', { showError: false }),

  /**
   * @description 查询一到三级分类和商品 id（页面加载，失败静默）
   * @returns {Promise<Array>} 分类与商品树列表
   */
  getCategoryProdTree: (config = {}) =>
    api.get('/prod/prod/getCategoryProd', { showError: false, ...config }),

  /**
   * @description 获取OSS预签名URL
   * @param {string} fileName - 文件名
   * @param {number} fileSize - 文件大小
   * @param {boolean} isImFile - 是否是IM文件
   * @returns {Promise<Object>} 包含 preSignUrl、requiredHeaders、fileId、filePath
   */
  getPreSignUrl: (fileName, fileSize, isImFile = false) => {
    return api.get('/p/file/getPreSignUrl', {
      params: {
        fileName,
        fileSize,
        isImFile,
      },
    })
  },

  /**
   * @description 上传文件到OSS（使用预签名URL）
   * @param {string} preSignUrl - OSS预签名URL
   * @param {File} file - 文件对象
   * @param {Object} requiredHeaders - 预签名要求原样发送的请求头
   * @returns {Promise} 上传响应
   */
  uploadFileToOss: async (preSignUrl, file, requiredHeaders = {}) => {
    // 直接使用axios上传到OSS（不使用api实例，因为preSignUrl是完整的OSS URL）
    return axios.put(preSignUrl, file, {
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
        ...requiredHeaders,
      },
    })
  },

  /**
   * @description 通知服务器文件上传成功
   * @param {string} fileId - 文件ID
   * @param {number} fileSize - 文件大小
   * @param {number} attachFileGroupId - 附件分组ID（可选）
   * @returns {Promise<Object>} 包含 filePath 的响应
   */
  uploadSuccess: (fileId, fileSize, attachFileGroupId = 0) => {
    return api.put('/p/file/uploadSuccess', [
      {
        fileId,
        attachFileGroupId,
        fileSize,
      },
    ])
  },

  /**
   * @description 根据文件ID获取文件信息（含 filePath）
   * @param {string|number} fileId - 文件ID
   * @returns {Promise<Object>} 包含 filePath 的文件信息
   */
  getFileById: (fileId) =>
    api.get('/p/file/get_file_by_id', {
      params: { fileId },
    }),

  /**
   * @description 获取反馈类型枚举列表
   * @param {string} type - 类型集合，例如 "10,11"
   * @param {Object} config - 额外请求配置
   * @returns {Promise<Object>} 枚举列表
   */
  getTypeValueList: (type, config = {}) => {
    return api.get('/common/typeValue/get', {
      params: { type },
      showError: false,
      ...config,
    })
  },
}
